import React, { useEffect, useState, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';

import {
    MdFirstPage,
    MdLastPage,
    MdOutlineChevronLeft,
    MdOutlineChevronRight,
    MdOutlineMoreHoriz,
} from 'react-icons/md';

const normalizeSearch = (search = '') => {
    const params = new URLSearchParams(search.replace(/^\?/, ''));
    const sortedParams = new URLSearchParams([...params.entries()].sort(([a], [b]) => a.localeCompare(b)));
    const result = sortedParams.toString();

    return result ? `?${result}` : '';
};

function PaginationSkeleton() {
    return (
        <>
            <nav className="pagination-desk d-none d-md-flex" aria-label="Pagination" aria-busy="true">
                <span className="pagination-desk__link" aria-hidden="true">
                    <Skeleton width={20} height={20} borderRadius={4} />
                </span>
                <span className="pagination-desk__link" aria-hidden="true">
                    <Skeleton width={12} height={20} borderRadius={4} />
                </span>
                <span className="pagination-desk__link" aria-hidden="true">
                    <Skeleton width={12} height={20} borderRadius={4} />
                </span>
                <span className="pagination-desk__link active-page" aria-hidden="true">
                    <Skeleton width={18} height={24} borderRadius={4} baseColor="#4BBDFA" highlightColor="#C3E9FD" />
                </span>
                <span className="pagination-desk__link" aria-hidden="true">
                    <Skeleton width={12} height={20} borderRadius={4} />
                </span>
                <span className="pagination-desk__link" aria-hidden="true">
                    <Skeleton width={12} height={20} borderRadius={4} />
                </span>
                <span className="pagination-desk__link" aria-hidden="true">
                    <Skeleton width={20} height={20} borderRadius={4} />
                </span>
            </nav>
            <nav className="pagination-mob-wrap d-md-none" aria-label="Pagination" aria-busy="true">
                <div className="pagination-mob">
                    <span className="pagination-mob__link pagination-mob__link--disabled" aria-hidden="true">
                        <Skeleton width={20} height={20} borderRadius={4} />
                    </span>
                    <span className="pagination-mob__link pagination-mob__link--text" aria-hidden="true">
                        <Skeleton width={20} height={20} borderRadius={4} />
                        <Skeleton width={45} height={16} borderRadius={4} />
                    </span>
                    <span className="pagination-mob__link active-page" aria-hidden="true">
                        <Skeleton width={18} height={24} borderRadius={4} baseColor="#4BBDFA" highlightColor="#C3E9FD" />
                    </span>
                    <span className="pagination-mob__link pagination-mob__link--text" aria-hidden="true">
                        <Skeleton width={45} height={16} borderRadius={4} />
                        <Skeleton width={20} height={20} borderRadius={4} />
                    </span>
                    <span className="pagination-mob__link pagination-mob__link--disabled" aria-hidden="true">
                        <Skeleton width={20} height={20} borderRadius={4} />
                    </span>
                </div>
                <p className="pagination-mob-info" aria-hidden="true">
                    <Skeleton width={75} height={16} borderRadius={4} />
                    <Skeleton width={16} height={16} borderRadius={4} />
                </p>
            </nav>
        </>
    );
}

function Pagination({
    currentPage,
    totalPages,
    pathname,
    search,
    data,
    trackingParams = [],
    onPageClick,
    // DESC: navigationWithoutWindowPathnameAndSearch omogućava korištenje paginacije i kad ne radimo stvarno navigiranje preko window obj.
    // Koristimo kad navigiranje ide samo preko state-a.
    navigationWithoutWindowPathnameAndSearch = false,
}) {
    const twigPaginationData = !data && windowManipulationService.isBrowserEnvironment() && window?.AppConfig?.paginationData;
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    const locationSearch = useMemo(
        () => normalizeSearch(search),
        [search],
    );

    const pages = useMemo(() => {
        if (totalPages <= 5) {
            return Array.from(
                { length: totalPages },
                (_, index) => index + 1,
            );
        }
        if (currentPage <= 3) {
            return [1, 2, 3, 4, '...', totalPages];
        }
        if (currentPage >= totalPages - 2) {
            return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        }

        return [
            1,
            '...',
            currentPage - 1,
            currentPage,
            currentPage + 1,
            '...',
            totalPages,
        ];
    }, [currentPage, totalPages]);

    const createHref = (page) => {
        const urlParams = new URLSearchParams(locationSearch);
        trackingParams.forEach((param) => { urlParams.delete(param); });
        urlParams.delete('page');

        if (page === 1) {
            let href = pathname;
            const query = urlParams.toString();
            if (query) {
                href += `?${query}`;
            }

            return href;
        }
        urlParams.set('page', page);

        return `?${urlParams.toString()}`;
    };

    const handlePageClick = (event, page) => {
        if (onPageClick) {
            if (navigationWithoutWindowPathnameAndSearch) {
                event.preventDefault();
            }
            onPageClick(page);
        }
    };

    const renderPageLink = (page, index) => {
        if (typeof page === 'number') {
            if (page === currentPage) {
                return (
                    <span
                        key={`page-${page}`}
                        className="pagination-desk__link active-page"
                        aria-current="page"
                    >
                        {page}
                    </span>
                );
            }

            return (
                <a
                    key={`page-${page}`}
                    href={createHref(page)}
                    className="pagination-desk__link"
                    aria-label={`Go to page ${page}`}
                    onClick={(e) => handlePageClick(e, page)}
                >
                    {page}
                </a>
            );
        }

        return (
            <span key={`ellipsis-${index}`} aria-hidden="true">
                <MdOutlineMoreHoriz className="pagination-desk__ellipsis-icon" />
            </span>
        );
    };

    const renderPaginationText = ({ text, temporaryText, skeletonWidth, suffix = '' }) => {
        if (text) {
            return (
                <span>
                    <span>{text}</span>
                    <span>{suffix}</span>
                </span>
            );
        }

        if (temporaryText) {
            return (
                <span data-adriatic-temp="temporary">
                    <span>{temporaryText}</span>
                    <span>{suffix}</span>
                </span>
            );
        }

        return (
            <span aria-hidden="true"><Skeleton width={skeletonWidth} /></span>
        );
    };

    if (!isHydrated) {
        return <PaginationSkeleton />;
    }

    return (
        <>
            {/* Desktop pagination */}
            <nav className="pagination-desk d-none d-md-flex" aria-label="Pagination">
                {currentPage > 1 && (
                    <a
                        href={createHref(currentPage - 1)}
                        className="pagination-desk__link"
                        aria-label={`Go to page ${currentPage - 1}`}
                        onClick={(e) => handlePageClick(e, currentPage - 1)}
                    >
                        <MdOutlineChevronLeft className="pagination-desk__link-icon" aria-hidden="true" />
                    </a>
                )}
                {pages.map(renderPageLink)}
                {currentPage < totalPages && (
                    <a
                        href={createHref(currentPage + 1)}
                        className="pagination-desk__link"
                        aria-label={`Go to page ${currentPage + 1}`}
                        onClick={(e) => handlePageClick(e, currentPage + 1)}
                    >
                        <MdOutlineChevronRight className="pagination-desk__link-icon" aria-hidden="true" />
                    </a>
                )}
            </nav>
            {/* Mobile pagination */}
            <nav className="pagination-mob-wrap d-md-none" aria-label="Pagination">
                <div className="pagination-mob">
                    {totalPages === 1 ? (
                        <span
                            className="pagination-mob__link active-page"
                            aria-current="page"
                        >
                            {currentPage}
                        </span>
                    ) : (
                        <>
                            {currentPage === 1 ? (
                                <span className="pagination-mob__link pagination-mob__link--disabled" aria-hidden="true">
                                    <MdFirstPage className="pagination-mob__icon" aria-hidden="true" />
                                </span>
                            ) : (
                                <a
                                    href={createHref(1)}
                                    className="pagination-mob__link"
                                    aria-label="Go to page 1"
                                    onClick={(e) => handlePageClick(e, 1)}
                                >
                                    <MdFirstPage className="pagination-mob__icon" aria-hidden="true" />
                                </a>
                            )}
                            {currentPage === 1 ? (
                                <span className="pagination-mob__link pagination-mob__link--text pagination-mob__link--disabled" aria-hidden="true">
                                    <MdOutlineChevronLeft className="pagination-mob__icon" aria-hidden="true" />
                                    {renderPaginationText({
                                        text: data?.previousPageText,
                                        temporaryText: twigPaginationData?.previous,
                                        skeletonWidth: 45,
                                    })}
                                </span>
                            ) : (
                                <a
                                    href={createHref(currentPage - 1)}
                                    className="pagination-mob__link pagination-mob__link--text"
                                    aria-label={`Go to page ${currentPage - 1}`}
                                    onClick={(e) => handlePageClick(e, currentPage - 1)}
                                >
                                    <MdOutlineChevronLeft className="pagination-mob__icon" aria-hidden="true" />
                                    {renderPaginationText({
                                        text: data?.previousPageText,
                                        temporaryText: twigPaginationData?.previous,
                                        skeletonWidth: 45,
                                    })}
                                </a>
                            )}
                            <span
                                className="pagination-mob__link active-page"
                                aria-current="page"
                            >
                                {currentPage}
                            </span>
                            {currentPage === totalPages ? (
                                <span className="pagination-mob__link pagination-mob__link--text pagination-mob__link--disabled" aria-hidden="true">
                                    {renderPaginationText({
                                        text: data?.nextPageText,
                                        temporaryText: twigPaginationData?.next,
                                        skeletonWidth: 45,
                                    })}
                                    <MdOutlineChevronRight className="pagination-mob__icon" aria-hidden="true" />
                                </span>
                            ) : (
                                <a
                                    href={createHref(currentPage + 1)}
                                    className="pagination-mob__link pagination-mob__link--text"
                                    aria-label={`Go to page ${currentPage + 1}`}
                                    onClick={(e) => handlePageClick(e, currentPage + 1)}
                                >
                                    {renderPaginationText({
                                        text: data?.nextPageText,
                                        temporaryText: twigPaginationData?.next,
                                        skeletonWidth: 45,
                                    })}
                                    <MdOutlineChevronRight className="pagination-mob__icon" aria-hidden="true" />
                                </a>
                            )}
                            {currentPage === totalPages ? (
                                <span className="pagination-mob__link pagination-mob__link--disabled" aria-hidden="true">
                                    <MdLastPage className="pagination-mob__icon" aria-hidden="true" />
                                </span>
                            ) : (
                                <a
                                    href={createHref(totalPages)}
                                    className="pagination-mob__link"
                                    aria-label={`Go to page ${totalPages}`}
                                    onClick={(e) => handlePageClick(e, totalPages)}
                                >
                                    <MdLastPage className="pagination-mob__icon" aria-hidden="true" />
                                </a>
                            )}
                        </>
                    )}
                </div>
                {totalPages > 1 && (
                    <p className="pagination-mob-info">
                        {renderPaginationText({
                            text: data?.totalPagesText,
                            temporaryText: twigPaginationData?.total,
                            skeletonWidth: 75,
                            suffix: ': ',
                        })}
                        <span>{totalPages}</span>
                    </p>
                )}
            </nav>
        </>
    );
}

export default Pagination;
